"use client";

import Image from "next/image";
import { useTranslations } from "next-intl";

import { getChampionImageUrl } from "@/shared/lib/champion-image";
import { scoreColor, scoreGradient } from "@/shared/lib/score-color";
import { cn } from "@/shared/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/ui/popover";

/**
 * KAN-876: оценка по играм — столбик на игру (эпик KAN-804).
 *
 * Балл здесь НЕ ТОТ ЖЕ, что в остальных секциях, и это не мелочь. Агрегат сравнивает
 * игрока с игроками роли за турнир; здесь каждая игра сравнивается с такими же
 * играми той же роли. Средний по столбикам не обязан совпадать с баллом в кольце, и
 * подпись над блоком обязана это сказать — иначе расхождение читается как ошибка.
 *
 * Компонент принимает готовую вью-модель, а не ответ ручки: перевод ответа в неё —
 * работа контейнера. Так блок можно проверять тестами без мока сети.
 */

export interface ProfileGame {
  /** Устойчивый ключ строки «игрок × игра». */
  gameId: string;
  /** Балл за игру 0–100. null — сравнить эту игру было не с чем. */
  score: number | null;
  /** Чемпион. null — реплей не донёс имя; столбик остаётся, иконки нет. */
  champion?: string | null;
  /**
   * Исход игры. KAN-899 сделал поле nullable в контракте: null означает «это не срез
   * по игре», а не поражение.
   *
   * `win ?? false` здесь был бы дефектом того же класса, что мы ловим весь эпик —
   * неизвестное молча стало бы проигрышем. Поэтому третье состояние: прочерк.
   * На практике из `/replay-metrics/games` исход приходит всегда, но тип обязан
   * допускать отсутствие, иначе он врёт про контракт.
   */
  win?: boolean | null;
  /**
   * KAN-769: коды причин «игра с оговоркой» — AFK, ливер, ранняя сдача. Пусто или
   * не передано — игра целая.
   *
   * Наличие флагов НЕ повод спрятать игру: она сыграна и входит в историю игрока.
   * Флаги объясняют, почему у неё нет оценки, а не заменяют её отсутствие.
   */
  integrityFlags?: string[] | null;
}

/**
 * Почему у игры нет оценки. Причина берётся из флагов целостности, если они есть:
 * «оценки нет» и «оценки нет, потому что кто-то вышел из игры» — разные ответы, и
 * второй читателю полезнее. Флагов нет — говорим честно, что сравнить было не с чем.
 */
function unscoredReason(
  game: ProfileGame,
  t: (key: string, values?: Record<string, string | number>) => string,
): string {
  const flags = (game.integrityFlags ?? []).filter(Boolean);
  return flags.length > 0
    ? t("gameFlagged", { flags: flags.join(", ") })
    : t("gameUnscored");
}

/** Высота поля столбиков из макета. */
const TRACK_HEIGHT = 96;

/**
 * Минимальная видимая высота столбика, доля. Балл около нуля иначе схлопывается в
 * невидимую полоску, и игра выглядит пропущенной, хотя она сыграна и оценена.
 */
const MIN_BAR = 0.06;

export function ProfileGameBars({ games }: { games: ProfileGame[] }) {
  const t = useTranslations("TournamentStats.playerProfile");

  // ИГРЫ БЕЗ БАЛЛА НЕ ПРЯЧУТСЯ, А РИСУЮТСЯ СЕРЫМИ. Столбики читаются как путь по
  // турниру: пять игр — пять столбиков. Убери четвёртую, и читатель увидит четыре
  // и достроит неверную картину, причём уверенно: пропуск не выглядит пропуском.
  //
  // Серый столбик отвечает на оба вопроса сразу — игра была, оценки у неё нет.
  // «Нет оценки» и «нет игры» это разные утверждения, и витрина обязана их
  // различать; та же логика, что у прочерка вместо места при пустой группе
  // сравнения. Прежняя версия отсеивала такие игры и молча укорачивала историю.
  if (games.length === 0) return null;

  const unscored = games.filter((game) => game.score == null).length;

  return (
    <div>
      <div className="mb-3 flex items-baseline gap-2">
        {/* KAN-900: текст оговорки убран со страницы по просьбе заказчика, но САМ
            ФАКТ остался — он не косметика. Балл за игру и балл в списке считаются
            по разным группам сравнения, и у игрока без агрегатного балла читатель
            видит «балла нет» и «баллы 36 и 53» одновременно; без объяснения это
            выглядит арифметической ошибкой страницы.

            Popover, а не Tooltip: по наведению на телефоне подсказку не открыть, а
            нужна она именно там, где числа стоят рядом. */}
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="text-[11px] font-bold uppercase tracking-[0.12em] text-muted-foreground underline decoration-dotted underline-offset-4 transition-colors hover:text-foreground"
            >
              {t("gamesTitle")}
            </button>
          </PopoverTrigger>
          <PopoverContent className="max-w-[320px] text-[13px] leading-snug">
            {t("gamesNote", { count: games.length })}
            {unscored > 0 ? ` ${t("gamesUnscored", { count: unscored })}` : ""}
          </PopoverContent>
        </Popover>
      </div>

      <div className="flex items-end gap-2.5">
        {games.map((game) => {
          const score = game.score;
          // Неоценённая игра занимает своё место в ряду, но высоты не изображает:
          // любая высота была бы утверждением о качестве, которого мы не измеряли.
          const height =
            score == null
              ? MIN_BAR * TRACK_HEIGHT
              : Math.max(MIN_BAR, Math.min(100, score) / 100) * TRACK_HEIGHT;

          return (
            <div
              key={game.gameId}
              className="flex min-w-0 flex-1 flex-col items-center gap-[7px]"
            >
              <span
                className={cn(
                  "flex-none font-heading text-[15px] font-semibold tabular-nums",
                  score == null && "text-muted-foreground",
                )}
                style={score != null ? { color: scoreColor(score) } : undefined}
                title={score == null ? unscoredReason(game, t) : undefined}
              >
                {score == null ? "—" : Math.round(score)}
              </span>

              <span
                className="flex w-full flex-none items-end"
                style={{ height: `${TRACK_HEIGHT}px` }}
              >
                <span
                  className={cn(
                    "block w-full flex-none rounded-b-[3px] rounded-t-[9px]",
                    score == null
                      ? "border border-dashed border-muted-foreground/40 bg-muted"
                      : "shadow-[inset_0_1px_0_rgba(255,255,255,.28)]",
                  )}
                  style={
                    score == null
                      ? { height: `${height}px` }
                      : { height: `${height}px`, background: scoreGradient(score) }
                  }
                  title={score == null ? unscoredReason(game, t) : undefined}
                />
              </span>

              <span className="flex flex-none items-center gap-[5px]">
                {game.champion ? (
                  <span className="relative block h-[18px] w-[18px] shrink-0 overflow-hidden rounded">
                    <Image
                      src={getChampionImageUrl(game.champion)}
                      alt={game.champion}
                      title={game.champion}
                      width={18}
                      height={18}
                      className="size-full scale-[1.15] object-cover"
                      unoptimized
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.visibility = "hidden";
                      }}
                    />
                  </span>
                ) : null}
                <span
                  className={cn(
                    "text-[10px] font-bold",
                    game.win == null
                      ? "text-muted-foreground"
                      : game.win
                        ? "text-green-400"
                        : "text-red-400",
                  )}
                >
                  {game.win == null ? "—" : game.win ? t("win") : t("loss")}
                </span>
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
